import type {
    MailListItemCondensedThreeColumnProps,
    MailListItemSharedProps,
} from './MailListItemComponentProps';
import { LazyCategoryWell } from 'owa-categories';
import onCategoryClicked from '../utils/onCategoryClicked';
import classnames from 'owa-classnames';
import { observer } from 'owa-mobx-react';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import type { TableView, MailFolderTableQuery } from 'owa-mail-list-store';
import getListViewStore from 'owa-mail-list-store/lib/store/Store';
import { isMailListConversationView } from 'owa-mail-list-store';
import { isUnifiedInboxMailboxInfo } from 'owa-client-types/lib/UnifiedInboxMailboxInfo';
import { type SortColumn } from 'owa-mail-list-store/lib/store/schema/SortBy';
import {
    full,
    medium,
    compact,
    compactDensityUpdate,
    condensedItemFirstRowThreeColumnNext,
    isCondensedPinnedRow,
    highTwistyStyles,
    multiSelectNoTwistyDisplayedCondensed,
    firstColumnThreeColumn,
    firstColumnThreeColumnNoTwisty,
    condensedHighTwisty,
    condensedHighTwistyFull,
    condensedHighTwistyFullNoCheckbox,
    condensedHighTwistyMedium,
    condensedHighTwistyMediumNoCheckbox,
    condensedHighTwistyCompact,
    column,
    multiSelectNoCheckbox,
    secondLineTextThreeColumn,
    categoryContainerThreeColumn,
    categoryWellThreeColumn,
    multiSelectNoCheckbox_deprecated,
} from 'owa-mail-listitem-styles/lib/scss/MailListItem.scss';
import {
    condensedRowText,
    condensedItemFirstRow,
    condensedItemFirstRowTextThreeColumn,
} from './MailListItemCondensedThreeColumn.scss';
import { useMessageListTextStyle } from 'owa-mail-listitem-styles/lib/utils/useMessageListTextStyle';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import {
    showPersonaCheckbox,
    useIsTypeRampUpdatesEnabled,
    useMailListItemContainerContext,
} from 'owa-mail-list-item-shared';
import isSenderImageEnabled from 'owa-messagelist-layout-option/lib/utils/isSenderImageEnabled';
import React from 'react';
import {
    MailListItemFirstLineText,
    MailListItemIconBar,
    MailListItemLabelsContainer,
    MailListItemLineText,
    MailListItemTwistyButton,
    MailListItemUnreadCount,
    PersonaCheckbox,
    MailListItemMarker,
    MailListItemUnreadBar,
    FolderTag,
} from 'owa-mail-list-item-components';
import { getDensityModeCssClass, getDensityModeString, getIsDarkTheme } from 'owa-fabric-theme';
import { CopilotInboxPriorityIcon } from 'owa-mail-copilot-inbox-shared';

const defaultExport: React.FunctionComponent<MailListItemSharedProps> = observer(
    function MailListItemCondensedThreeColumn(
        props: MailListItemCondensedThreeColumnProps
    ): JSX.Element {
        const {
            canArchive,
            canDelete,
            canPin,
            categories,
            copilotInboxScore,
            copilotPrioritizeOnDeliveryOn,
            effectiveMentioned,
            firstLineText,
            firstLineTooltipText,
            hasAttachment,
            hasSharepointLink,
            highlightTerms,
            importance,
            isComplete,
            isFirstLevelExpanded,
            isFlagged,
            isInArchiveFolder,
            isInNotesFolder,
            isInOutboxFolder,
            isPinned,
            renderMinimum,
            isSearchTable,
            isSecondLevelExpanded,
            isLoadingSecondLevelExpansion,
            isSelected,
            isSnoozed,
            isTaggedForBigScreen,
            isUnauthenticatedSender,
            itemClassIcon,
            lastDeliveryTimestamp,
            lastSender,
            mailboxGuids,
            parentFolderDisplayName,
            parentFolderId,
            rowId,
            rowKey,
            secondLineText,
            secondLineTooltipText,
            shouldShowExternalLabel,
            shouldShowTwisty,
            showDraftsIndicator,
            showFolderTag,
            showPreviewText,
            smimeType,
            supportsFlagging,
            supportsHoverIcons,
            supportsMove,
            supportsPinning,
            tableViewId,
            isReminderSet,
            isScheduledSend,
            isProactiveDraft,
            unreadCount,
            bulkCategory,
        } = props;

        const isDenselessEnabled = useIsTypeRampUpdatesEnabled();
        const densityModeString = getDensityModeCssClass(
            full,
            medium,
            isDenselessEnabled ? compactDensityUpdate : compact
        );
        const tableView: TableView | undefined = getListViewStore().tableViews.get(
            tableViewId
        ) as TableView;

        // derived properties based on props/flights
        const shouldAllowMultiLinePreview = isGlobalFeatureEnabled('tri-mlMultiLinePreview');
        const mailTableQuery = tableView?.tableQuery as MailFolderTableQuery;
        const isCategorySort = mailTableQuery?.sortBy?.sortColumn === 14;
        const isUnread = unreadCount > 0;
        const showCategories = categories?.length > 0 && !isCategorySort;
        const showLabelsContainer = showFolderTag || showCategories;

        const { conditionalFormattingModifications } = useMailListItemContainerContext();
        const mailboxInfo = props.mailboxInfo ?? getMailboxInfo(tableView);
        const isConversationView = isMailListConversationView(tableView);

        const unifiedInboxMailboxSmtpTooltip = isUnifiedInboxMailboxInfo(getMailboxInfo(tableView))
            ? mailboxInfo?.mailboxSmtpAddress
            : undefined;
        const showMultiSelectButton = !isSearchTable;
        const senderImageEnabled = isSenderImageEnabled();
        const showCheckbox = showPersonaCheckbox(isSearchTable, tableView);
        const isDarkTheme = getIsDarkTheme();

        const twistyComponent = (
            <MailListItemTwistyButton
                densityMode={getDensityModeString()}
                isDarkTheme={isDarkTheme}
                isFirstLevelExpanded={isFirstLevelExpanded}
                renderMinimum={renderMinimum}
                isSecondLevelExpanded={isSecondLevelExpanded}
                isLoadingSecondLevelExpansion={isLoadingSecondLevelExpansion}
                isSelected={isSelected}
                isSingleLine={false}
                isUnread={unreadCount > 0}
                rowKey={rowKey}
                tableViewId={tableViewId}
            />
        );

        const renderSecondLineText = (
            additionalStyles: {
                [key: string]: string;
            },
            text: string,
            tooltipText?: string
        ) => {
            return (
                <MailListItemLineText
                    additionalStyles={additionalStyles}
                    addOverflowStyles={false}
                    highlightTerms={highlightTerms}
                    text={text}
                    tooltipText={tooltipText}
                    isDarkTheme={isDarkTheme}
                    conditionalFormattingModifications={conditionalFormattingModifications}
                />
            );
        };

        const unreadBar = React.useMemo(() => {
            return (
                <MailListItemUnreadBar
                    isUnread={isUnread}
                    rowKey={rowKey}
                    tableViewId={tableViewId}
                    isDarkTheme={isDarkTheme}
                />
            );
        }, [isUnread, rowKey, tableViewId, isDarkTheme]);

        return (
            <>
                {unreadBar}
                <div
                    className={classnames(
                        densityModeString,
                        isCondensedPinnedRow,
                        highTwistyStyles,
                        !isConversationView &&
                            showMultiSelectButton &&
                            !senderImageEnabled &&
                            !showCheckbox &&
                            multiSelectNoTwistyDisplayedCondensed
                    )}
                    title={unifiedInboxMailboxSmtpTooltip}
                >
                    {
                        <div
                            className={classnames(
                                firstColumnThreeColumn,
                                isConversationView &&
                                    !shouldShowTwisty &&
                                    firstColumnThreeColumnNoTwisty,
                                condensedHighTwisty,
                                getDensityModeCssClass(
                                    showCheckbox
                                        ? condensedHighTwistyFull
                                        : condensedHighTwistyFullNoCheckbox,
                                    showCheckbox
                                        ? condensedHighTwistyMedium
                                        : condensedHighTwistyMediumNoCheckbox,
                                    condensedHighTwistyCompact
                                )
                            )}
                        >
                            {shouldShowTwisty && twistyComponent}
                            {showCheckbox && (
                                <PersonaCheckbox
                                    isCondensedView={true}
                                    isSelected={isSelected}
                                    isUnauthenticatedSender={isUnauthenticatedSender}
                                    lastDeliveryTimestamp={lastDeliveryTimestamp}
                                    lastSender={lastSender}
                                    rowKey={rowKey}
                                    tableViewId={tableViewId}
                                />
                            )}
                        </div>
                    }
                    <div
                        className={classnames(
                            column,
                            !showCheckbox &&
                                (isGlobalFeatureEnabled('tri-mlCheckboxBehavior')
                                    ? multiSelectNoCheckbox
                                    : multiSelectNoCheckbox_deprecated)
                        )}
                    >
                        <div
                            className={classnames(
                                condensedItemFirstRowThreeColumnNext,
                                densityModeString
                            )}
                        >
                            <div className={condensedItemFirstRowTextThreeColumn}>
                                <div
                                    className={classnames(
                                        condensedRowText,
                                        useMessageListTextStyle('Major')
                                    )}
                                >
                                    <div className={condensedItemFirstRow}>
                                        <MailListItemMarker
                                            isUnauthenticatedSender={isUnauthenticatedSender}
                                            shouldShowExternalLabel={shouldShowExternalLabel}
                                            isSelected={isSelected}
                                            bulkCategory={bulkCategory}
                                        />
                                        <MailListItemFirstLineText
                                            highlightTerms={highlightTerms}
                                            showDraftsIndicator={showDraftsIndicator}
                                            isInOutboxFolder={isInOutboxFolder}
                                            unreadCount={unreadCount}
                                            isScheduledSend={isScheduledSend}
                                            isProactiveDraft={isProactiveDraft}
                                            text={firstLineText}
                                            tooltipText={firstLineTooltipText ?? undefined}
                                            isSingleLineView={false}
                                            conditionalFormattingModifications={
                                                conditionalFormattingModifications
                                            }
                                            isDarkTheme={isDarkTheme}
                                            isSelected={isSelected}
                                            isUnread={isUnread}
                                        />
                                    </div>
                                    <div
                                        className={classnames(
                                            secondLineTextThreeColumn,
                                            densityModeString,
                                            useMessageListTextStyle('Major')
                                        )}
                                    >
                                        <CopilotInboxPriorityIcon
                                            copilotInboxScore={copilotInboxScore}
                                            scenario="ML"
                                            mailboxInfo={mailboxInfo}
                                            isUnread={isUnread}
                                            copilotPrioritizeOnDeliveryOn={
                                                copilotPrioritizeOnDeliveryOn
                                            }
                                        />
                                        {shouldAllowMultiLinePreview && showCategories && (
                                            <LazyCategoryWell
                                                key={rowId}
                                                categoryWellContainerClass={categoryWellThreeColumn}
                                                categoryContainerClass={
                                                    categoryContainerThreeColumn
                                                }
                                                categories={categories}
                                                onCategoryClicked={onCategoryClicked}
                                                mailboxInfo={mailboxInfo}
                                            />
                                        )}
                                        {renderSecondLineText(
                                            {},
                                            secondLineText,
                                            secondLineTooltipText ?? undefined
                                        )}
                                        {shouldAllowMultiLinePreview && showFolderTag && (
                                            <FolderTag
                                                folderId={parentFolderId}
                                                parentFolderDisplayName={parentFolderDisplayName}
                                                isPreviewTextOn={showPreviewText}
                                                isSingleLine={false}
                                                mailboxGuids={mailboxGuids}
                                                shouldBeInTabOrder={isSelected}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                            {!isFirstLevelExpanded && (
                                <MailListItemUnreadCount
                                    unreadCount={unreadCount}
                                    isDarkTheme={isDarkTheme}
                                />
                            )}
                            <MailListItemIconBar
                                canArchive={canArchive}
                                canDelete={canDelete}
                                canPin={canPin}
                                effectiveMentioned={effectiveMentioned}
                                hasAttachment={hasAttachment}
                                hasSharepointLink={hasSharepointLink}
                                importance={importance}
                                isComplete={isComplete}
                                isFlagged={isFlagged}
                                isInArchiveFolder={isInArchiveFolder}
                                isInNotesFolder={isInNotesFolder}
                                isInOutboxFolder={isInOutboxFolder}
                                isPinned={isPinned}
                                isSingleLine={false}
                                isSnoozed={isSnoozed}
                                isTaggedForBigScreen={isTaggedForBigScreen}
                                itemClassIcon={itemClassIcon}
                                neverShowHoverIcons={!supportsHoverIcons}
                                neverShowPropertyIcons={false}
                                rowKey={rowKey}
                                smimeType={smimeType}
                                supportsFlagging={supportsFlagging}
                                supportsMove={supportsMove}
                                supportsPinning={supportsPinning}
                                tableViewId={tableViewId}
                                unreadCount={unreadCount}
                                isReminderSet={isReminderSet}
                                hideImportancePropertyIcon={false}
                                hideFlagPropertyIcon={false}
                            />
                        </div>
                        {!shouldAllowMultiLinePreview && showLabelsContainer && (
                            <MailListItemLabelsContainer
                                categories={categories}
                                categoryContainerClass={categoryContainerThreeColumn}
                                categoryWellContainerClass={categoryWellThreeColumn}
                                isSingleLine={false}
                                mailboxGuids={mailboxGuids}
                                parentFolderDisplayName={parentFolderDisplayName}
                                parentFolderId={parentFolderId}
                                rowId={rowId}
                                showCategories={showCategories}
                                showFolderTag={showFolderTag}
                                showPreviewText={showPreviewText}
                                mailboxInfo={mailboxInfo}
                            />
                        )}
                    </div>
                </div>
            </>
        );
    },
    'MailListItemCondensedThreeColumn'
);
export default defaultExport;
